import { Injectable, inject, signal } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthService } from './auth.service';
import { TokenRefreshCoordinator } from './token-refresh.coordinator';

/**
 * Status da sessão
 */
export enum SessionStatus {
  ACTIVE = 'ACTIVE',
  EXPIRING_SOON = 'EXPIRING_SOON',
  EXPIRED = 'EXPIRED'
}

/**
 * Serviço de monitoramento de sessão
 *
 * Responsabilidades:
 * - Monitorar expiração de token
 * - Renovar token automaticamente via TokenRefreshCoordinator (mutex + falha limpa)
 * - Alertar usuário quando sessão está expirando
 */
@Injectable({
  providedIn: 'root'
})
export class SessionMonitorService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly authService = inject(AuthService);
  private readonly tokenRefresh = inject(TokenRefreshCoordinator);

  private monitorSubscription: Subscription | null = null;
  private readonly sessionStatus = signal<SessionStatus>(SessionStatus.ACTIVE);
  private readonly timeUntilExpiry = signal<number>(0); // em segundos

  readonly status = this.sessionStatus.asReadonly();
  readonly expiryTime = this.timeUntilExpiry.asReadonly();

  /**
   * Inicia monitoramento de sessão
   */
  startMonitoring(): void {
    if (this.monitorSubscription) {
      return;
    }

    // Verifica a cada 30 segundos
    this.monitorSubscription = interval(30000).subscribe(() => {
      this.checkSession();
    });

    // Verifica imediatamente
    this.checkSession();
  }

  /**
   * Para monitoramento de sessão
   */
  stopMonitoring(): void {
    if (this.monitorSubscription) {
      this.monitorSubscription.unsubscribe();
      this.monitorSubscription = null;
    }
  }

  /**
   * Verifica estado da sessão
   */
  private checkSession(): void {
    if (!this.authService.isAuthenticated()) {
      this.sessionStatus.set(SessionStatus.EXPIRED);
      return;
    }

    if (this.tokenRefresh.isReauthInProgress()) {
      this.sessionStatus.set(SessionStatus.EXPIRED);
      return;
    }

    // Obtém dados do token
    this.oidcSecurityService.getPayloadFromAccessToken().subscribe({
      next: (payload: any) => {
        if (!payload || !payload.exp) {
          return;
        }

        const expiryTime = payload.exp * 1000; // converter para ms
        const now = Date.now();
        const timeLeft = Math.floor((expiryTime - now) / 1000); // em segundos

        this.timeUntilExpiry.set(timeLeft);

        if (timeLeft <= 0) {
          this.handleExpiredSession();
        } else if (timeLeft <= 300) {
          this.sessionStatus.set(SessionStatus.EXPIRING_SOON);
        } else {
          this.sessionStatus.set(SessionStatus.ACTIVE);
        }
      },
      error: (err) => {
        console.error('[SESSION] Erro ao verificar token:', err);
      }
    });
  }

  /**
   * Trata sessão expirada — renovação via coordenador (sem loop de /token).
   */
  private handleExpiredSession(): void {
    console.log('[SESSION] Sessão expirada');
    this.sessionStatus.set(SessionStatus.EXPIRED);

    this.tokenRefresh.refreshSession().subscribe({
      next: (result) => {
        if (result.isAuthenticated) {
          console.log('[SESSION] Token renovado com sucesso');
          this.sessionStatus.set(SessionStatus.ACTIVE);
        }
        // Se falhou, o coordenador já limpou storage e redirecionou ao login
      },
      error: () => {
        // Falha já tratada pelo TokenRefreshCoordinator
      }
    });
  }

  /**
   * Força renovação de sessão (uso manual)
   */
  refreshSession(): void {
    this.tokenRefresh.refreshSession().subscribe({
      next: (result) => {
        if (result.isAuthenticated) {
          console.log('[SESSION] Sessão renovada manualmente');
          this.sessionStatus.set(SessionStatus.ACTIVE);
        }
      },
      error: (err) => {
        console.error('[SESSION] Erro ao renovar sessão:', err);
      }
    });
  }
}
